import { useState } from "react";
import { ListItem, UnorderedList } from "@chakra-ui/react";
import { getFormattedDistance, getFormattedDuration } from "../../utilities/formattingHelper";
import { InsightsPanelHeading, InsightsPanelStack, InsightsPanelText } from "./InsightsPanelComponentsGroup";
import InsightsPanelGridItem from "./InsightsPanelGridItem";
import InsightsPanelTable from "./InsightsPanelTable";
import InsightPanelTypeRadioSelect from "./InsightPanelTypeRadioSelect";
import InsightsPanelPieChart from "./InsightsPanelPieChart";


export function SummaryInsightsPanelItem({
    noOfJourneys, totalDistance, averageDistance, timesAroundTheWorld, timesToTheMoon
}) {
    return (
        <InsightsPanelGridItem
            header={<>
                <InsightsPanelHeading>
                    {noOfJourneys}
                    {noOfJourneys !== 1 ? ' journeys' : ' journey'} over {getFormattedDistance(totalDistance, 'km')}km
                </InsightsPanelHeading>
            </>}
        >
            <InsightsPanelStack>
                <InsightsPanelText>
                    Average distance: <strong>{getFormattedDistance(averageDistance, 'km')}km</strong>
                </InsightsPanelText>
                <InsightsPanelText>
                    <strong>{timesAroundTheWorld.toFixed(3)}x</strong> around the world
                </InsightsPanelText>
                <InsightsPanelText>
                    <strong>{timesToTheMoon.toFixed(3)}x</strong> the distance to the moon
                </InsightsPanelText>
            </InsightsPanelStack>
        </InsightsPanelGridItem>
    )
}

export function DurationInsightsPanelItem({
    totalDuration, averageDuration, fullyFormattedDuration,
    isolatedMinutesDuration, isolatedHoursDuration, isolatedDaysDuration, isolatedWeeksDuration, isolatedMonthsDuration,
}) {
    return (
        <InsightsPanelGridItem
            header={<>
                <InsightsPanelHeading>
                    {getFormattedDuration(totalDuration)} total duration
                </InsightsPanelHeading>
            </>}
        >
            <InsightsPanelText>
                Average duration: <strong>{getFormattedDuration(averageDuration.toFixed(0))}</strong>
            </InsightsPanelText>

            <InsightsPanelText>
                An aggregate of <strong>{fullyFormattedDuration}</strong>
            </InsightsPanelText>

            <InsightsPanelText>In isolation, it's:</InsightsPanelText>
            <UnorderedList>
                <ListItem>{isolatedMinutesDuration} minutes</ListItem>
                <ListItem>{isolatedHoursDuration.toFixed(1)} hours</ListItem>
                <ListItem>{isolatedDaysDuration.toFixed(2)} days</ListItem>
                <ListItem>{isolatedWeeksDuration.toFixed(2)} weeks</ListItem>
                <ListItem>{isolatedMonthsDuration.toFixed(4)} months</ListItem>
            </UnorderedList>
        </InsightsPanelGridItem>
    )
}

export function StationsVisitedInsightsPanelItem({
    noOfStationsVisited, topStations
}) {
    return (
        <InsightsPanelGridItem
            header={<>
                <InsightsPanelHeading>
                    {noOfStationsVisited}
                    {noOfStationsVisited !== 1 ? ' stations' : ' station'} visited
                </InsightsPanelHeading>
            </>}
        >
            <InsightsPanelTable
                title='Most visited stations'
                column1Heading='Station'
                column2Heading='№'
                data={topStations.map(({ station, count }) => ({
                    col1: station.name,  // Assuming station objects have a 'name' property
                    col2: count
                }))}
            />
        </InsightsPanelGridItem>
    )
}

export function TopRoutesInsightsPanelItem({
    topRoutes
}) {
    return (
        <InsightsPanelGridItem
            header={<InsightsPanelHeading>Top routes</InsightsPanelHeading>}
        >
            <InsightsPanelTable
                column1Heading='Route'
                column2Heading='№'
                data={topRoutes.map(({ route, count }) => ({
                    col1: route,
                    col2: count
                }))}
            />
        </InsightsPanelGridItem>
    )
}

export function LongestJourneysInsightsPanelItem({
    longestDistanceJourneys, longestDurationJourneys,
}) {
    const [categorySelection, setCategorySelection] = useState('distance')

    return (
        <InsightsPanelGridItem
            header={<InsightsPanelHeading>Longest journeys</InsightsPanelHeading>}
            footer={<>
                <InsightPanelTypeRadioSelect
                    setSelectedValue={(value) => setCategorySelection(value)}
                />
            </>}
        >
            {categorySelection === 'distance' && (
                <InsightsPanelTable
                    column1Heading='Route'
                    column2Heading='Distance'
                    data={longestDistanceJourneys.map(({ route, distance }) => ({
                        col1: route,
                        col2: `${getFormattedDistance(distance, 'km')}km`
                    }))}
                />
            )}
            {categorySelection === 'duration' && (
                <InsightsPanelTable
                    column1Heading='Route'
                    column2Heading='Duration'
                    data={longestDurationJourneys.map(({ route, duration }) => ({
                        col1: route,
                        col2: getFormattedDuration(duration)
                    }))}
                />
            )}
        </InsightsPanelGridItem>
    )
}

export function ShortestJourneysInsightsPanelItem({
    shortestDistanceJourneys, shortestDurationJourneys,
}) {
    const [categorySelection, setCategorySelection] = useState('distance')

    return (
        <InsightsPanelGridItem
            header={<InsightsPanelHeading>Shortest journeys</InsightsPanelHeading>}
            footer={<>
                <InsightPanelTypeRadioSelect
                    setSelectedValue={(value) => setCategorySelection(value)}
                />
            </>}
        >
            {categorySelection === 'distance' && (
                <InsightsPanelTable
                    column1Heading='Route'
                    column2Heading='Distance'
                    data={shortestDistanceJourneys.map(({ route, distance }) => ({
                        col1: route,
                        col2: `${getFormattedDistance(distance, 'km')}km`
                    }))}
                />
            )}
            {categorySelection === 'duration' && (
                <InsightsPanelTable
                    column1Heading='Route'
                    column2Heading='Duration'
                    data={shortestDurationJourneys.map(({ route, duration }) => ({
                        col1: route,
                        col2: getFormattedDuration(duration)
                    }))}
                />
            )}
        </InsightsPanelGridItem>
    )
}

export function CountriesVisitedInsightsPanelItem({
    noOfCountriesVisitedByRail, countriesVisitedByRail
}) {
    return (
        <InsightsPanelGridItem
            header={<>
                <InsightsPanelHeading>
                    {noOfCountriesVisitedByRail}
                    {noOfCountriesVisitedByRail !== 1 ? ' countries' : ' country'}
                </InsightsPanelHeading>
            </>}
        >
            <InsightsPanelTable
                column1Heading='Country'
                column2Heading='№'
                column2HeadingTooltip='This column shows the total count of journeys for each country, calculated as both departures and arrivals (i.e. a journey entirely within one country is counted twice – once for departure and once for arrival.)'
                data={countriesVisitedByRail.map(({ country, count }) => ({
                    col1: country.name,
                    col2: count,
                }))}
            />


        </InsightsPanelGridItem>
    )
}

export function InternationalTravelInsightsPanelItem({
    noOfJourneysWithinHomeCountry, noOfJourneysToOrFromAbroad, noOfJourneysOutsideHomeCountry,
    distanceWithinHomeCountry, distanceToOrFromAbroad, distanceOutsideHomeCountry,
    durationWithinHomeCountry, durationToOrFromAbroad, durationOutsideHomeCountry,
}) {
    const [categorySelection, setCategorySelection] = useState('segments')

    return (
        <InsightsPanelGridItem
            header={<>
                <InsightsPanelHeading>International travel</InsightsPanelHeading>
            </>}
            footer={<>
                <InsightPanelTypeRadioSelect
                    enableSegments
                    setSelectedValue={(value) => setCategorySelection(value)}
                />
            </>}
        >
            {categorySelection === 'segments' && (
                <>
                    <InsightsPanelPieChart
                        data={[
                            { name: 'Within home country', value: noOfJourneysWithinHomeCountry },
                            { name: 'To or from abroad', value: noOfJourneysToOrFromAbroad },
                            { name: 'Outside home country', value: noOfJourneysOutsideHomeCountry }
                        ]}
                    />
                    <InsightsPanelStack>
                        <InsightsPanelText>
                            <strong>{noOfJourneysWithinHomeCountry}</strong>
                            {noOfJourneysWithinHomeCountry !== 1 ? ' journeys' : ' journey'} within your home country
                        </InsightsPanelText>
                        <InsightsPanelText>
                            <strong>{noOfJourneysToOrFromAbroad}</strong>
                            {noOfJourneysToOrFromAbroad !== 1 ? ' journeys' : ' journey'} to or from abroad
                        </InsightsPanelText>
                        <InsightsPanelText>
                            <strong>{noOfJourneysOutsideHomeCountry}</strong>
                            {noOfJourneysOutsideHomeCountry !== 1 ? ' journeys' : ' journey'} outside your home country
                        </InsightsPanelText>
                    </InsightsPanelStack>
                </>
            )}
            {categorySelection === 'distance' && (
                <>
                    <InsightsPanelPieChart
                        data={[
                            { name: 'Within home country', value: distanceWithinHomeCountry },
                            { name: 'To or from abroad', value: distanceToOrFromAbroad },
                            { name: 'Outside home country', value: distanceOutsideHomeCountry }
                        ]}
                    />

                    <InsightsPanelStack>
                        <InsightsPanelText><strong>{getFormattedDistance(distanceWithinHomeCountry, 'km')}km</strong> travelled within your home country</InsightsPanelText>
                        <InsightsPanelText><strong>{getFormattedDistance(distanceToOrFromAbroad, 'km')}km</strong> travelled to or from abroad</InsightsPanelText>
                        <InsightsPanelText><strong>{getFormattedDistance(distanceOutsideHomeCountry, 'km')}km</strong> travelled outside your home country</InsightsPanelText>
                    </InsightsPanelStack>
                </>
            )}
            {categorySelection === 'duration' && (
                <>
                    <InsightsPanelPieChart
                        data={[
                            { name: 'Within home country', value: durationWithinHomeCountry },
                            { name: 'To or from abroad', value: durationToOrFromAbroad },
                            { name: 'Outside home country', value: durationOutsideHomeCountry }
                        ]}
                    />
                    <InsightsPanelStack>
                        <InsightsPanelText><strong>{getFormattedDuration(durationWithinHomeCountry)}</strong> spent travelling within your home country</InsightsPanelText>
                        <InsightsPanelText><strong>{getFormattedDuration(durationToOrFromAbroad)}</strong> spent travelling to or from abroad</InsightsPanelText>
                        <InsightsPanelText><strong>{getFormattedDuration(durationOutsideHomeCountry)}</strong> spent travelling outside your home country</InsightsPanelText>
                    </InsightsPanelStack>
                </>
            )}
        </InsightsPanelGridItem>
    )
}

export function StationCountryBreakdownInsightsPanelItem({
    noOfStationsVisitedInHomeCountry, noOfStationsVisitedAbroad,
}) {
    return (
        <InsightsPanelGridItem
            header={<InsightsPanelHeading>Stations at home & abroad</InsightsPanelHeading>}
        >
            <InsightsPanelPieChart
                data={[
                    { name: 'Home country', value: noOfStationsVisitedInHomeCountry },
                    { name: 'Abroad', value: noOfStationsVisitedAbroad }
                ]}
            />

            <InsightsPanelStack>
                <InsightsPanelText>
                    <strong>{noOfStationsVisitedInHomeCountry}</strong>
                    {noOfStationsVisitedInHomeCountry !== 1 ? ' stations' : ' station'} visited in your home country
                </InsightsPanelText>
                <InsightsPanelText>
                    <strong>{noOfStationsVisitedAbroad}</strong>
                    {noOfStationsVisitedAbroad !== 1 ? ' stations' : ' station'} visited abroad
                </InsightsPanelText>
            </InsightsPanelStack>
        </InsightsPanelGridItem>
    )
}


{/* Currently not uzed */ }
{/* <InsightsPanelGridItem
                        header={<InsightsPanelHeading>Domestic v. international</InsightsPanelHeading>}
                        footer={<>
                            <InsightPanelTypeRadioSelect
                                enableSegments
                                setSelectedValue={(value) => setCategorySelection({ ...categorySelection, domesticOrInternational: value })}
                            />
                        </>}
                    >


                        {categorySelection.domesticOrInternational === 'segments' && (
                            <>
                                <InsightsPanelPieChart
                                    data={[
                                        { name: 'Domestic', value: noOfDomesticJourneys },
                                        { name: 'International', value: noOfInternationalJourneys }
                                    ]}
                                />

                                <Text>
                                    {noOfDomesticJourneys} domestic
                                    {noOfDomesticJourneys !== 1 ? ' journeys' : ' journey'}
                                </Text>

                                <Text>
                                    {noOfInternationalJourneys} international
                                    {noOfInternationalJourneys !== 1 ? ' journeys' : ' journey'}
                                </Text>
                            </>
                        )}
                        {categorySelection.domesticOrInternational === 'distance' && (
                            <>
                                <InsightsPanelPieChart
                                    data={[
                                        { name: 'Domestic', value: distanceDomestic },
                                        { name: 'International', value: distanceInternational }
                                    ]}
                                />

                                <Text>
                                    {getFormattedDistance(distanceDomestic, 'km')} km travelled domestically
                                </Text>
                                <Text>
                                    {getFormattedDistance(distanceInternational, 'km')} km travelled internationally
                                </Text>
                            </>
                        )}
                        {categorySelection.domesticOrInternational === 'duration' && (
                            <>
                                <InsightsPanelPieChart
                                    data={[
                                        { name: 'Domestic', value: durationDomestic },
                                        { name: 'International', value: durationInternational }
                                    ]}
                                />
                                <Text>
                                    {getFormattedDuration(durationDomestic)} spent travelling domestically
                                </Text>
                                <Text>
                                    {getFormattedDuration(durationInternational)} spent travelling internationally
                                </Text>
                            </>
                        )}
                    </InsightsPanelGridItem> */}

